import type {
  ConfidenceBand,
  MetricStatus,
  MetricUnit,
  MetricValue,
  QueryMetadata,
  QueryResult,
} from '@aurora-pvi/contracts';
import type {
  AttributionState,
  ClaimedContribution,
  DeliveryCoverage,
  OutcomeMovement,
  OutcomeReleaseRef,
  OutcomeSeriesPoint,
  OutcomeTraceChain,
  OutcomeTraceabilityView,
  OutcomeWithoutDelivery,
  OutcomeWorkItemRef,
  ProductOutcomeRef,
  TraceLink,
  TracedWorkItem,
  UnlinkedWorkGroup,
} from '@aurora-pvi/contracts';

/**
 * Outcome traceability fixtures (UC-09-066).
 *
 * Hand-authored rather than derived from `buildAuroraDataset`, for the same
 * reason the estate fixtures are: the generator emits work items, releases and
 * metric series, but it does not emit the thing this screen is about — a
 * *judgement* that a particular release moved a particular outcome, and the
 * evidence behind that judgement. Deriving those by joining on dates would
 * manufacture exactly the timing-only attribution the screen exists to warn
 * about, and it would look computed.
 *
 * Products, teams and source systems are the seeded Aurora Airlines ones, so a
 * reader moving between Product 360 and this screen sees one airline.
 *
 * The populations reconcile, and the reconciliation is the screen's headline:
 *
 *   1,284 shipped work items
 *     = 214 declared + 331 reference-inferred + 122 timing-only + 617 unlinked
 *
 *   617 unlinked = 268 no outcome in release notes
 *                + 141 regulatory (legitimately outcome-free)
 *                + 128 keep-the-lights-on (legitimately outcome-free)
 *                +  80 outcome field left at its default
 *
 * Six shapes of answer are represented deliberately, because a screen that only
 * ever renders successes has not been tested against the cases that matter:
 * a measured movement, a movement consistent with the claim but confounded, a
 * claim with nothing measuring it, a shipped release whose metric did not move,
 * an attribution refused outright because the metric was redefined mid-window,
 * and outcomes with no delivery behind them at all.
 */

// ── Deterministic ids ────────────────────────────────────────────────────────

/**
 * A stable v4-shaped UUID from a readable seed. The contracts type these ids as
 * UUIDs, so 'outcome-cdr-01' would type-check and fail the moment the schema was
 * run over it. Same seed, same id, so a selection survives a reload.
 */
function uuidFrom(seed: string): string {
  let h1 = 0x811c9dc5;
  let h2 = 0x01000193;
  for (let index = 0; index < seed.length; index += 1) {
    h1 = Math.imul(h1 ^ seed.charCodeAt(index), 0x01000193) >>> 0;
    h2 = Math.imul(h2 + seed.charCodeAt(index) * (index + 11), 0x85ebca6b) >>> 0;
  }
  // `>>> 0` on every term, not just the sum: `h1 ^ h2` is a *signed* 32-bit int
  // in JavaScript, and a negative one stringifies as '-7b3c…', which produces a
  // hyphen in the middle of the id and fails `z.string().uuid()` at parse time
  // rather than at compile time.
  const hex = (value: number) => (value >>> 0).toString(16).padStart(8, '0');
  const body = `${hex(h1)}${hex(h2)}${hex(h1 ^ h2)}${hex(h1 + h2)}`;
  return [
    body.slice(0, 8),
    body.slice(8, 12),
    `4${body.slice(13, 16)}`,
    `a${body.slice(17, 20)}`,
    body.slice(20, 32),
  ].join('-');
}

/** The dataset's fixed "today", matching `@aurora-pvi/seed` and the estate fixtures. */
const AS_OF = '2026-05-20';
const REFRESHED_AT = '2026-05-20T02:15:00.000Z';
const PERIOD_LABEL = 'FY26 H1 — 1 Jan to 20 May 2026';

// ── Governed metric values ───────────────────────────────────────────────────

interface MetricOptions {
  status?: MetricStatus;
  unavailableReason?: string | null;
  desiredDirection?: MetricValue['desiredDirection'];
  confidence?: ConfidenceBand;
  qualityScore?: number | null;
  comparisonValue?: MetricValue['comparisonValue'];
  comparisonLabel?: string | null;
  definitionVersion?: string;
  refreshedAt?: string | null;
  periodStart?: string;
}

function metric(
  metricId: string,
  label: string,
  value: MetricValue['value'],
  unit: MetricUnit,
  options: MetricOptions = {},
): MetricValue {
  const status = options.status ?? (value === null ? 'Unavailable' : 'Available');
  return {
    metricId,
    label,
    subjectId: null,
    subjectType: null,
    value: status === 'Available' || status === 'Partial' ? value : null,
    unit,
    periodStart: options.periodStart ?? '2026-01-01',
    periodEnd: AS_OF,
    comparisonValue: options.comparisonValue ?? null,
    comparisonLabel: options.comparisonLabel ?? null,
    status,
    definitionVersion: options.definitionVersion ?? 'v4.0',
    sourceMode: 'Calculated',
    sourceRunId: null,
    refreshedAt: options.refreshedAt === undefined ? REFRESHED_AT : options.refreshedAt,
    qualityScore: options.qualityScore ?? 74,
    confidence: options.confidence ?? 'Medium',
    desiredDirection: options.desiredDirection ?? 'Context',
    sensitivity: 'Internal',
    allowedDrilldowns: [],
    unavailableReason: options.unavailableReason ?? null,
  };
}

/**
 * The response envelope. `isSyntheticData` is true and every screen prints it:
 * an attribution screen that hid the fact its evidence was invented would be the
 * single worst thing in this repository.
 */
function outcomesMeta(options: {
  correlationId: string;
  sources: Array<{ name: string; status: MetricStatus; recordCount: number | null }>;
  qualityScore: number;
  openIssueCount: number;
  confidence: ConfidenceBand;
}): QueryMetadata {
  const overall: MetricStatus = options.sources.some((source) => source.status === 'Stale')
    ? 'Stale'
    : options.sources.some((source) => source.status === 'Partial')
      ? 'Partial'
      : 'Available';

  return {
    correlationId: options.correlationId,
    periodStart: '2026-01-01',
    periodEnd: AS_OF,
    comparisonPeriodStart: '2025-10-01',
    comparisonPeriodEnd: '2025-12-31',
    asOf: null,
    sourceStatus: {
      overall,
      refreshedAt: REFRESHED_AT,
      lastGoodAt: null,
      failedRunAt: null,
      contributingSources: options.sources.map((source) => ({
        sourceSystem: source.name,
        status: source.status,
        refreshedAt: REFRESHED_AT,
        recordCount: source.recordCount,
      })),
    },
    quality: {
      score: options.qualityScore,
      completeness: 0.72,
      validity: 0.91,
      uniqueness: 0.94,
      consistency: 0.79,
      timeliness: 0.88,
      openIssueCount: options.openIssueCount,
    },
    confidence: options.confidence,
    scope: {
      scopeId: uuidFrom('scope:enterprise'),
      scopeType: 'Enterprise',
      scopeLabel: 'Aurora Airlines — enterprise',
      isNarrowed: false,
      maskedFields: [],
    },
    allowedActions: [
      { action: 'outcomes.export', permission: 'export', enabled: true, disabledReason: null },
      {
        action: 'outcomes.declareLink',
        permission: 'edit',
        enabled: false,
        disabledReason:
          'Declaring a link writes to the Product Registry, which this build has no connection to. The control prepares the declaration and stops.',
      },
      {
        action: 'outcomes.approveAttribution',
        permission: 'approve',
        enabled: false,
        disabledReason:
          'Publishing an attribution needs the Value Steward role; an attribution is a claim about spend once it is published.',
      },
    ],
    isSyntheticData: true,
  };
}

// ── Products, as the seeded portfolio names them ─────────────────────────────

function product(businessCode: string, name: string) {
  return {
    id: uuidFrom(`product:${businessCode}`),
    businessCode,
    name,
    route: `/products/${uuidFrom(`product:${businessCode}`)}`,
  };
}

const CDR = product('PRD-CDR', 'Crew Disruption Recovery');
const BAGJ = product('PRD-BAGJ', 'Baggage Journey');
const BOOK = product('PRD-BOOK', 'Flight Booking');
const FUEL = product('PRD-FUEL', 'Fuel Optimisation');
const DSCM = product('PRD-DSCM', 'Disruption Communications');
const LOYL = product('PRD-LOYL', 'Skyward Loyalty');
const TURN = product('PRD-TURN', 'Turn Management');

// ── Outcomes ─────────────────────────────────────────────────────────────────

function outcome(
  code: string,
  name: string,
  statement: string,
  productRef: ReturnType<typeof product>,
  ownerName: string,
  metricId: string,
  metricLabel: string,
  horizon: ProductOutcomeRef['horizon'] = 'Year',
): ProductOutcomeRef {
  return {
    id: uuidFrom(`outcome:${code}`),
    code,
    name,
    statement,
    product: productRef,
    ownerName,
    horizon,
    metricId,
    metricLabel,
  };
}

const OUT_CDR = outcome(
  'OUT-CDR-01',
  'Recover a disrupted roster inside the hour',
  'When the day goes wrong, the operation has a legal, flyable roster within one hour of the disruption being declared — without breaching duty limits or crew agreements.',
  CDR,
  'Marta Hensley, Director Network Operations',
  'MET-210',
  'Median roster recovery time',
);

const OUT_BAGJ = outcome(
  'OUT-BAGJ-01',
  'Fewer bags separated from their passenger',
  'Mishandled baggage falls below the IATA benchmark for a carrier of our size, measured on the industry rate rather than on our own complaint volume.',
  BAGJ,
  'Peter Vaardt, Head of Ground Operations',
  'MET-214',
  'Mishandled baggage rate',
);

const OUT_BOOK = outcome(
  'OUT-BOOK-01',
  'More of the people who look, book',
  'A traveller who reaches an Aurora fare page completes the booking more often than they did a year ago, without discounting to get there.',
  BOOK,
  'Ines Okafor, Director Digital Commerce',
  'MET-217',
  'Look-to-book conversion',
);

const OUT_FUEL = outcome(
  'OUT-FUEL-01',
  'Less fuel burned per block hour',
  'Fuel burn per block hour falls by 3% against FY25, at constant network and constant fleet mix.',
  FUEL,
  'Dr Sanne Broekhuis, Head of Flight Efficiency',
  'MET-221',
  'Fuel burn per block hour',
);

const OUT_DSCM = outcome(
  'OUT-DSCM-01',
  'Passengers hear it from us first',
  'A passenger affected by a disruption is told by Aurora before they have to ask, on the channel they chose.',
  DSCM,
  'Ines Okafor, Director Digital Commerce',
  'MET-224',
  'Proactive notification rate',
);

const OUT_LOYL = outcome(
  'OUT-LOYL-01',
  'Tier members fly Aurora more often',
  'Skyward members at Silver and above take a larger share of their annual flying with Aurora than in FY25.',
  LOYL,
  'Ines Okafor, Director Digital Commerce',
  'MET-231',
  'Member share of wallet',
  'MultiYear',
);

const OUT_TURN = outcome(
  'OUT-TURN-01',
  'Aircraft leave the stand on time',
  'On-time departure performance at the four base airports reaches 84% at the D0 gate.',
  TURN,
  'Peter Vaardt, Head of Ground Operations',
  'MET-236',
  'On-time departure (D0)',
  'Quarter',
);

// ── Round 6: coverage across the rest of the airline ─────────────────────────
//
// The first seven outcomes cluster around crew, baggage and the booking funnel,
// which left whole domains — line maintenance, dispatch, cargo, safety,
// sustainability, network — with nothing to look at. These ten spread the
// screen across the operation. Each carries a real movement series and a
// delivery chain, so none of them arrives as an empty panel.

const MRO = product('PRD-MRO', 'Aircraft Maintenance Planning');
const FPD = product('PRD-FPD', 'Flight Planning & Dispatch');
const PRM = product('PRD-PRM', 'Pricing & Revenue Management');
const CCC = product('PRD-CCC', 'Cold Chain Cargo');
const ANC = product('PRD-ANC', 'Ancillary & Retailing');
const CKI = product('PRD-CKI', 'Check-in & Boarding');
const CARE = product('PRD-CARE', 'Customer Care & Notifications');
const SMS = product('PRD-SMS', 'Safety Management');
const SUS = product('PRD-SUS', 'Fuel & Emissions Reporting');
const NET = product('PRD-NET', 'Network & Schedule Planning');

const OUT_MRO = outcome(
  'OUT-MRO-01',
  'Fewer aircraft grounded waiting for a part',
  'An aircraft held on the ground for a spare is the most expensive kind of idle. AOG hours per thousand flight hours fall against FY25 without raising the value of stock held at the bases.',
  MRO,
  'Ivan Petrov, Head of Maintenance Planning',
  'MET-241',
  'AOG hours per 1,000 flight hours',
);

const OUT_FPD = outcome(
  'OUT-FPD-01',
  'Flight plans survive contact with the day',
  'A plan filed at dispatch still holds at top of climb. Enroute re-plans fall, measured on plans changed after departure rather than on dispatcher workload.',
  FPD,
  'Dele Okonjo, Head of Flight Operations',
  'MET-244',
  'Enroute re-plan rate',
);

const OUT_PRM = outcome(
  'OUT-PRM-01',
  'Seats sell at the price the model intended',
  'Revenue per available seat kilometre rises against FY25 on constant capacity, without leaning on bag and seat fees to get there.',
  PRM,
  'Marcus Lindqvist, Director Revenue Management',
  'MET-247',
  'Revenue per available seat km (US cents)',
);

const OUT_CCC = outcome(
  'OUT-CCC-01',
  'Pharma arrives inside its temperature envelope',
  'Every temperature-controlled shipment stays inside its declared range from acceptance to delivery. Measured on the shipment, not on the container: a compliant box in a hot warehouse is still an excursion.',
  CCC,
  'Nora Sadiq, Head of Cargo',
  'MET-251',
  'Temperature excursion rate',
);

const OUT_ANC = outcome(
  'OUT-ANC-01',
  'Travellers buy the extras they actually wanted',
  'Ancillary attach rate rises while the refund rate on ancillaries stays flat — growth from relevance rather than from a checkout that catches people out.',
  ANC,
  'Ines Okafor, Director Digital Commerce',
  'MET-254',
  'Ancillary attach rate',
  'Quarter',
);

const OUT_CKI = outcome(
  'OUT-CKI-01',
  'Boarding without a queue',
  'A passenger at the gate spends under four minutes in the boarding queue at the four base airports, at the same load factors.',
  CKI,
  'Peter Vaardt, Head of Ground Operations',
  'MET-257',
  'Passengers boarding within four minutes',
);

const OUT_CARE = outcome(
  'OUT-CARE-01',
  'One contact is enough',
  'A passenger who contacts Aurora has their issue closed on that contact, without a second call, a repeat message or a hand-off to another queue.',
  CARE,
  'Chantelle Duval, Head of Customer Care',
  'MET-261',
  'First-contact resolution rate',
);

const OUT_SMS = outcome(
  'OUT-SMS-01',
  'Safety findings close before they age',
  'A Level 1 finding is closed inside 30 days of being raised. Measured on closure accepted by the Safety Review Board, not on a team marking its own action done.',
  SMS,
  'Capt. Rhys Morgan, Director Safety',
  'MET-264',
  'Median days to close a Level 1 finding',
);

const OUT_SUS = outcome(
  'OUT-SUS-01',
  'Less carbon per passenger flown',
  'CO2 per revenue passenger kilometre falls against FY25 at constant network and constant fleet mix, on measured uplift rather than on booked fuel.',
  SUS,
  'Dr Sanne Broekhuis, Head of Flight Efficiency',
  'MET-267',
  'CO2 grams per revenue passenger km',
  'MultiYear',
);

const OUT_NET = outcome(
  'OUT-NET-01',
  'The schedule we publish is the schedule we fly',
  'Completion factor across the published schedule reaches 99.3%, counting cancellations inside 72 hours of departure whatever the cause.',
  NET,
  'Priya Raman, VP Network Planning',
  'MET-271',
  'Schedule completion factor',
);

const OUTCOMES: ProductOutcomeRef[] = [
  OUT_CDR,
  OUT_BAGJ,
  OUT_BOOK,
  OUT_FUEL,
  OUT_DSCM,
  OUT_LOYL,
  OUT_TURN,
  OUT_MRO,
  OUT_FPD,
  OUT_PRM,
  OUT_CCC,
  OUT_ANC,
  OUT_CKI,
  OUT_CARE,
  OUT_SMS,
  OUT_NET,
  OUT_SUS,
];

// ── Work items and releases ──────────────────────────────────────────────────

function workItem(
  reference: string,
  title: string,
  kind: OutcomeWorkItemRef['kind'],
  teamName: string,
  closedAt: string | null,
  effortPoints: number | null,
  effortUnavailableReason: string | null = null,
): OutcomeWorkItemRef {
  return {
    id: uuidFrom(`work:${reference}`),
    reference,
    title,
    kind,
    teamName,
    closedAt,
    effortPoints,
    effortUnavailableReason,
    /*
      Null, and it stays null.

      A fixture work item came from nowhere, so naming a tool for it — or
      offering a link into one — would put a clickable falsehood on the screen
      whose whole subject is traceability. Only a record an ingestion run
      actually created carries these.
    */
    sourceSystem: null,
    sourceUrl: null,
  };
}

function release(
  code: string,
  name: string,
  productRef: ReturnType<typeof product>,
  plannedDate: string,
  actualDate: string | null,
  workItemCount: number,
  status: OutcomeReleaseRef['status'] = 'Released',
): OutcomeReleaseRef {
  return {
    id: uuidFrom(`release:${code}`),
    code,
    name,
    product: productRef,
    status,
    plannedDate,
    actualDate,
    workItemCount,
  };
}

// ── Links ────────────────────────────────────────────────────────────────────

/** A link a human asserted and is named for. Highest weight, still not proof. */
function declared(
  by: string,
  at: string,
  evidence: TraceLink['evidence'],
  caveat: string | null,
): TraceLink {
  return {
    type: 'DeclaredByTeam',
    confidence: 'High',
    confidenceScore: null,
    evidence,
    establishedBy: by,
    establishedAt: at,
    caveat,
  };
}

/** A link a rule read out of a field. Mechanical, checkable, occasionally wrong. */
function inferredFromReference(
  rule: string,
  at: string,
  score: number,
  evidence: TraceLink['evidence'],
  caveat: string | null,
): TraceLink {
  return {
    type: 'InferredFromReference',
    confidence: score >= 0.8 ? 'High' : score >= 0.55 ? 'Medium' : 'Low',
    confidenceScore: score,
    evidence,
    establishedBy: rule,
    establishedAt: at,
    caveat,
  };
}

/**
 * A link that rests on nothing but the calendar.
 *
 * Always Low, always caveated, and the caveat is not optional here: this is the
 * link type that, left unmarked, turns a traceability screen into a machine for
 * laundering coincidence into credit.
 */
function inferredFromTiming(rule: string, at: string, score: number, detail: string): TraceLink {
  return {
    type: 'InferredFromTiming',
    confidence: 'Low',
    confidenceScore: score,
    evidence: [
      {
        label: 'Shipped inside the outcome measurement window',
        detail,
        sourceSystem: 'Work Tracker',
        observedAt: at,
      },
    ],
    establishedBy: rule,
    establishedAt: at,
    caveat:
      'Nothing connects this release to the outcome except that both fall in the same window. Treat it as a candidate for a team to confirm or reject, never as a contribution.',
  };
}

// ── Series ───────────────────────────────────────────────────────────────────

function series(
  values: Array<[period: string, value: number | null]>,
  definitionVersion: string | ((period: string) => string),
  unavailableReason: string | null = null,
): OutcomeSeriesPoint[] {
  return values.map(([period, value]) => ({
    period,
    value,
    unavailableReason: value === null ? unavailableReason : null,
    definitionVersion:
      typeof definitionVersion === 'function' ? definitionVersion(period) : definitionVersion,
  }));
}

// ── Movements ────────────────────────────────────────────────────────────────

/**
 * 1. Crew Disruption Recovery — a real, measured movement.
 *
 * The only chain on this fixture entitled to the word "measured", and it earns
 * it by having a step change at a known release, a declared link, and a short,
 * assessed confounder list rather than an empty one.
 */
const MOVEMENT_CDR: OutcomeMovement = {
  metric: metric('MET-210', 'Median roster recovery time', 2.1, 'hours', {
    desiredDirection: 'Down',
    comparisonValue: 3.4,
    comparisonLabel: 'FY25 Q4 average',
    confidence: 'High',
    qualityScore: 88,
  }),
  baselineLabel: 'FY25 Q4 average (Oct–Dec 2025)',
  baselineValue: 3.4,
  baselineUnavailableReason: null,
  currentValue: 2.1,
  deltaAbsolute: -1.3,
  deltaPercent: -0.382,
  direction: 'Improved',
  target: 1.0,
  targetUnavailableReason: null,
  measurable: true,
  unmeasurableReason: null,
  definitionVersionAtBaseline: 'v4.0',
  definitionVersionNow: 'v4.0',
  definitionChangedAt: null,
  definitionChangeNote: null,
  series: series(
    [
      ['2025-09', 3.5],
      ['2025-10', 3.4],
      ['2025-11', 3.3],
      ['2025-12', 3.5],
      ['2026-01', 3.2],
      ['2026-02', 2.9],
      ['2026-03', 2.4],
      ['2026-04', 2.2],
      ['2026-05', 2.1],
    ],
    'v4.0',
  ),
  confounders: [
    {
      label: 'Winter disruption was milder than FY25',
      detail:
        'Fourteen fewer weather-cancelled days across the four bases. Smaller recoveries complete faster regardless of tooling, and no attempt has been made to normalise for this.',
      occurredAt: '2026-02-28',
    },
    {
      label: 'Two additional crew controllers joined the OCC in January',
      detail:
        'Headcount in the recovery desk rose from 9 to 11. Assessed as material by the Value Steward and not separated out.',
      occurredAt: '2026-01-12',
    },
  ],
};

/**
 * 2. Baggage Journey — the refusal.
 *
 * IATA restated the mishandled-baggage denominator in March 2026. The series
 * either side of that date is measuring two different things, so the "38%
 * improvement" a naive delta produces is an artefact of arithmetic across a
 * definition boundary. Every numeric field is null and the reason is the only
 * thing the screen may show.
 */
const MOVEMENT_BAGJ: OutcomeMovement = {
  metric: metric('MET-214', 'Mishandled baggage rate', null, 'ratio', {
    status: 'Unavailable',
    desiredDirection: 'Down',
    confidence: 'Low',
    qualityScore: 41,
    unavailableReason:
      'The metric definition changed on 12 March 2026. No value is published for a window that straddles the change.',
  }),
  baselineLabel: 'FY25 Q4 average (Oct–Dec 2025)',
  baselineValue: null,
  baselineUnavailableReason:
    'The baseline was computed under definition v3.2, which counted mishandled bags per thousand passengers. It cannot be restated under v4.0 without the per-bag denominator, which the ground handler did not supply before 2026.',
  currentValue: null,
  deltaAbsolute: null,
  deltaPercent: null,
  direction: 'NotMeasurable',
  target: null,
  targetUnavailableReason:
    'The committed target of 4.0 was set against the v3.2 definition and has not been restated by the outcome owner.',
  measurable: false,
  unmeasurableReason:
    'MET-214 changed definition inside the measurement window. Until 12 March 2026 it counted mishandled bags per thousand passengers; from that date it counts them per thousand bags carried, following the IATA 2026 restatement. The two series are not the same quantity, so their difference is not an improvement — it is a unit change. No attribution is computed for this outcome, and none should be quoted, until the outcome owner republishes a baseline on v4.0.',
  definitionVersionAtBaseline: 'v3.2',
  definitionVersionNow: 'v4.0',
  definitionChangedAt: '2026-03-12',
  definitionChangeNote:
    'IATA Resolution 753 alignment, adopted by Ground Operations on 12 March 2026. Denominator changed from passengers to bags carried; the numerator is unchanged.',
  series: series(
    [
      ['2025-09', 5.1],
      ['2025-10', 4.9],
      ['2025-11', 5.0],
      ['2025-12', 5.3],
      ['2026-01', 4.8],
      ['2026-02', 4.7],
      ['2026-03', 3.1],
      ['2026-04', 3.0],
      ['2026-05', 2.9],
    ],
    (period) => (period >= '2026-03' ? 'v4.0' : 'v3.2'),
  ),
  confounders: [
    {
      label: 'Ground handler changed at two bases',
      detail:
        'Swissport replaced the incumbent at OSL and CPH in February. Independent of any Aurora release.',
      occurredAt: '2026-02-01',
    },
  ],
};

/**
 * 3. Flight Booking — a movement that is consistent with the claim and does not
 * establish it. The confounder list is longer than the evidence list, which is
 * the honest shape for most commercial outcomes.
 */
const MOVEMENT_BOOK: OutcomeMovement = {
  metric: metric('MET-217', 'Look-to-book conversion', 0.0292, 'percent', {
    desiredDirection: 'Up',
    comparisonValue: 0.0241,
    comparisonLabel: 'FY25 Q4 average',
    confidence: 'Medium',
    qualityScore: 76,
  }),
  baselineLabel: 'FY25 Q4 average (Oct–Dec 2025)',
  baselineValue: 0.0241,
  baselineUnavailableReason: null,
  currentValue: 0.0292,
  deltaAbsolute: 0.0051,
  deltaPercent: 0.2116,
  direction: 'Improved',
  target: 0.03,
  targetUnavailableReason: null,
  measurable: true,
  unmeasurableReason: null,
  definitionVersionAtBaseline: 'v4.0',
  definitionVersionNow: 'v4.0',
  definitionChangedAt: null,
  definitionChangeNote: null,
  series: series(
    [
      ['2025-09', 0.0238],
      ['2025-10', 0.0241],
      ['2025-11', 0.0246],
      ['2025-12', 0.0239],
      ['2026-01', 0.0252],
      ['2026-02', 0.0249],
      ['2026-03', 0.0263],
      ['2026-04', 0.0288],
      ['2026-05', 0.0292],
    ],
    'v4.0',
  ),
  confounders: [
    {
      label: 'A seven-day fare sale ran across the April step',
      detail:
        'Marketing ran "Spring Anywhere" from 6–13 April. Conversion always rises during a sale; the release shipped on 9 April, inside it.',
      occurredAt: '2026-04-06',
    },
    {
      label: 'A competitor withdrew capacity on eleven overlapping routes',
      detail: 'Effective from the March schedule change. Not adjusted for.',
      occurredAt: '2026-03-29',
    },
    {
      label: 'Easter fell in April in 2026 and in March in 2025',
      detail:
        'The comparison window is not calendar-aligned for seasonal demand. Network Planning flagged this; no seasonal adjustment is applied to MET-217.',
      occurredAt: '2026-04-05',
    },
  ],
};

/**
 * 4. Fuel Optimisation — a claim with nothing measuring it. The tidiest number
 * on the screen (€4.1m) is the one with the least behind it, which is exactly
 * why the claim and the measurement are kept in separate panels.
 */
const MOVEMENT_FUEL: OutcomeMovement = {
  metric: metric('MET-221', 'Fuel burn per block hour', null, 'ratio', {
    status: 'Unavailable',
    desiredDirection: 'Down',
    confidence: 'Low',
    qualityScore: 38,
    refreshedAt: '2026-02-09T03:00:00.000Z',
    unavailableReason:
      'The ACARS fuel feed has not delivered since 9 February 2026. No value has been computed for March, April or May.',
  }),
  baselineLabel: 'FY25 full year',
  baselineValue: 2.94,
  baselineUnavailableReason: null,
  currentValue: null,
  deltaAbsolute: null,
  deltaPercent: null,
  direction: 'NotMeasurable',
  target: 2.85,
  targetUnavailableReason: null,
  measurable: false,
  unmeasurableReason:
    'There is a baseline but no current value: the ACARS fuel feed stopped delivering on 9 February 2026 and three of the five months in the window have no computed figure. A delta drawn from the two months that did arrive would compare a partial winter against a full year, so none is drawn.',
  definitionVersionAtBaseline: 'v3.9',
  definitionVersionNow: 'v3.9',
  definitionChangedAt: null,
  definitionChangeNote: null,
  series: series(
    [
      ['2025-09', 2.96],
      ['2025-10', 2.95],
      ['2025-11', 2.93],
      ['2025-12', 2.94],
      ['2026-01', 2.92],
      ['2026-02', 2.9],
      ['2026-03', null],
      ['2026-04', null],
      ['2026-05', null],
    ],
    'v3.9',
    'The ACARS fuel feed has not delivered since 9 February 2026.',
  ),
  confounders: [
    {
      label: 'Two A320ceo airframes left the fleet in January',
      detail:
        'Fleet mix is held constant in the outcome statement and was not, in fact, constant. Flight Efficiency is aware.',
      occurredAt: '2026-01-20',
    },
  ],
};

/**
 * 5. Disruption Communications — shipped, and the metric did not move. Reported
 * with the same weight a success would get; this is the case a traceability
 * screen is most tempted to bury.
 */
const MOVEMENT_DSCM: OutcomeMovement = {
  metric: metric('MET-224', 'Proactive notification rate', 0.64, 'percent', {
    desiredDirection: 'Up',
    comparisonValue: 0.63,
    comparisonLabel: 'FY25 Q4 average',
    confidence: 'High',
    qualityScore: 84,
  }),
  baselineLabel: 'FY25 Q4 average (Oct–Dec 2025)',
  baselineValue: 0.63,
  baselineUnavailableReason: null,
  currentValue: 0.64,
  deltaAbsolute: 0.01,
  deltaPercent: 0.0159,
  direction: 'Flat',
  target: 0.8,
  targetUnavailableReason: null,
  measurable: true,
  unmeasurableReason: null,
  definitionVersionAtBaseline: 'v4.0',
  definitionVersionNow: 'v4.0',
  definitionChangedAt: null,
  definitionChangeNote: null,
  series: series(
    [
      ['2025-09', 0.62],
      ['2025-10', 0.63],
      ['2025-11', 0.61],
      ['2025-12', 0.64],
      ['2026-01', 0.63],
      ['2026-02', 0.64],
      ['2026-03', 0.63],
      ['2026-04', 0.65],
      ['2026-05', 0.64],
    ],
    'v4.0',
  ),
  confounders: [],
};

// ── Chains ───────────────────────────────────────────────────────────────────

function traced(item: OutcomeWorkItemRef, link: TraceLink): TracedWorkItem {
  return { item, link };
}

const CHAINS_CDR: OutcomeTraceChain[] = [
  {
    id: uuidFrom('chain:cdr:R26.02-04'),
    outcome: OUT_CDR,
    release: release(
      'R26.02-04',
      'Recovery solver v3 — pairing repair',
      CDR,
      '2026-02-14',
      '2026-02-18',
      31,
    ),
    releaseLink: declared(
      'Anneke Rasmussen (Product Owner, Crew Recovery Squad)',
      '2026-02-19T09:12:00.000Z',
      [
        {
          label: 'Release note names OUT-CDR-01 as the target outcome',
          detail: 'Product Registry field `targetOutcome` = OUT-CDR-01, set before the release.',
          sourceSystem: 'Product Registry',
          observedAt: '2026-02-10T14:03:00.000Z',
        },
        {
          label: 'Pre-registered hypothesis with a stated success threshold',
          detail:
            'Recorded 10 Feb: "median recovery under 2.5h within 60 days, or we roll the solver back".',
          sourceSystem: 'Product Registry',
          observedAt: '2026-02-10T14:05:00.000Z',
        },
      ],
      'The link was declared before the release shipped, which rules out choosing the outcome after seeing the number. It does not rule out the other things that changed in the same window.',
    ),
    workItems: [
      traced(
        workItem(
          'AUR-14822',
          'Pairing repair heuristic — reuse legal standby crew before deadheading',
          'Feature',
          'Crew Recovery Squad',
          '2026-02-11',
          21,
        ),
        inferredFromReference(
          'rule:commit-key-scan@v2',
          '2026-02-18T22:40:00.000Z',
          0.94,
          [
            {
              label: 'Nine commits on the release branch carry the key',
              detail: 'AUR-14822 appears in 9 of 41 commits in R26.02-04.',
              sourceSystem: 'Work Tracker',
              observedAt: '2026-02-18T22:40:00.000Z',
            },
          ],
          null,
        ),
      ),
      traced(
        workItem(
          'AUR-14903',
          'Duty-limit checker: evaluate the whole repaired roster, not the changed legs',
          'Feature',
          'Crew Platform',
          '2026-02-13',
          13,
        ),
        inferredFromReference(
          'rule:commit-key-scan@v2',
          '2026-02-18T22:40:00.000Z',
          0.89,
          [
            {
              label: 'Referenced in the release manifest',
              detail: 'Listed under "included work" in the R26.02-04 manifest.',
              sourceSystem: 'Work Tracker',
              observedAt: '2026-02-18T22:40:00.000Z',
            },
          ],
          null,
        ),
      ),
      traced(
        workItem(
          'AUR-15011',
          'Recovery desk: show the solver’s three best options side by side',
          'Enhancement',
          'Crew Recovery Squad',
          '2026-02-16',
          null,
          'The Crew Recovery Squad stopped estimating in November 2025. Effort is unknown for their items, not zero.',
        ),
        declared(
          'Anneke Rasmussen (Product Owner, Crew Recovery Squad)',
          '2026-02-19T09:12:00.000Z',
          [
            {
              label: 'Added to the release by the owner after the manifest was cut',
              detail: 'No commit key; the link exists because the owner said so and signed it.',
              sourceSystem: 'Product Registry',
              observedAt: '2026-02-19T09:12:00.000Z',
            },
          ],
          null,
        ),
      ),
    ],
    unlinkedWorkItemCount: 28,
    claim: {
      claimedSharePercent: 0.7,
      shareUnavailableReason: null,
      statement:
        'The squad claims roughly 70% of the recovery-time improvement, on the grounds that the step change begins in the week the solver shipped and no other change to the recovery path was made in that window.',
      claimedBy: 'Anneke Rasmussen (Product Owner, Crew Recovery Squad)',
      claimedAt: '2026-04-02T11:20:00.000Z',
      basis: 'TeamAssertion',
      corroborated: true,
      corroborationNote:
        'Network Operations reviewed the claim on 18 April and accepted the direction while disputing the size — they attribute part of it to the two additional controllers. The 70% figure is the squad’s, not a jointly agreed number.',
    },
    claimAbsentReason: null,
    verdict: {
      state: 'Measured',
      headline: 'Measured movement, strong link',
      statement:
        'The link was declared before the release shipped, against a pre-registered threshold, and the metric crossed that threshold. Confounders were listed and one of them — two extra controllers in the OCC from January — is material and unquantified. So: the movement is real and the release is the best available explanation for most of it. The 70% share remains the delivery team’s claim and has not been measured.',
      refusalReason: null,
      confidence: 'High',
    },
  },
  {
    id: uuidFrom('chain:cdr:R26.03-09'),
    outcome: OUT_CDR,
    release: release(
      'R26.03-09',
      'Crew notification batching',
      CDR,
      '2026-03-20',
      '2026-03-24',
      12,
    ),
    releaseLink: inferredFromTiming(
      'rule:window-overlap@v1',
      '2026-05-19T02:15:00.000Z',
      0.22,
      'Shipped 24 March 2026, inside the 1 Jan – 20 May window in which MET-210 fell. No release note, commit or registry field mentions OUT-CDR-01.',
    ),
    workItems: [
      traced(
        workItem(
          'AUR-15288',
          'Batch roster-change notifications to one push per crew member per event',
          'Enhancement',
          'Crew Platform',
          '2026-03-19',
          8,
        ),
        inferredFromReference(
          'rule:commit-key-scan@v2',
          '2026-03-24T20:10:00.000Z',
          0.91,
          [
            {
              label: 'Referenced in four commits on the release branch',
              detail: 'AUR-15288, R26.03-09.',
              sourceSystem: 'Work Tracker',
              observedAt: '2026-03-24T20:10:00.000Z',
            },
          ],
          null,
        ),
      ),
    ],
    unlinkedWorkItemCount: 11,
    claim: null,
    claimAbsentReason:
      'No team has claimed a contribution for this release. It appears here only because a timing rule proposed it, and it is shown so that the proposal can be rejected rather than silently counted.',
    verdict: {
      state: 'Refused',
      headline: 'Timing only — no attribution',
      statement:
        'This release is connected to the outcome by nothing except the calendar. Notification batching plausibly affects how quickly crew acknowledge a change; whether it affects the time to produce a legal roster is not something anybody has argued, let alone measured.',
      refusalReason:
        'The only link is `InferredFromTiming`. An attribution computed from a timing-only link would be indistinguishable from an attribution computed from a real one, so none is computed. A member of the Crew Platform team can confirm or reject the proposal.',
      confidence: 'Low',
    },
  },
];

const CHAINS_BAGJ: OutcomeTraceChain[] = [
  {
    id: uuidFrom('chain:bagj:R26.01-07'),
    outcome: OUT_BAGJ,
    release: release(
      'R26.01-07',
      'Bag-tag rescan at transfer belts',
      BAGJ,
      '2026-01-22',
      '2026-01-27',
      19,
    ),
    releaseLink: declared(
      'Tomas Lindqvist (Product Owner, Baggage Engineering)',
      '2026-01-28T08:40:00.000Z',
      [
        {
          label: 'Release note names OUT-BAGJ-01',
          detail: 'Product Registry field `targetOutcome` = OUT-BAGJ-01.',
          sourceSystem: 'Product Registry',
          observedAt: '2026-01-20T10:00:00.000Z',
        },
      ],
      'The link is sound. The measurement it points at is not — see the movement panel.',
    ),
    workItems: [
      traced(
        workItem(
          'AUR-14401',
          'Rescan every transfer bag at the belt head, not at the sorter',
          'Feature',
          'Baggage Engineering',
          '2026-01-19',
          34,
        ),
        inferredFromReference(
          'rule:commit-key-scan@v2',
          '2026-01-27T19:30:00.000Z',
          0.96,
          [
            {
              label: 'Twelve commits carry the key',
              detail: 'AUR-14401, R26.01-07.',
              sourceSystem: 'Work Tracker',
              observedAt: '2026-01-27T19:30:00.000Z',
            },
          ],
          null,
        ),
      ),
      traced(
        workItem(
          'AUR-14455',
          'Reconcile the handler’s scan file against ours nightly',
          'TechnicalDebt',
          'Baggage Engineering',
          '2026-01-21',
          13,
        ),
        inferredFromReference(
          'rule:commit-key-scan@v2',
          '2026-01-27T19:30:00.000Z',
          0.72,
          [
            {
              label: 'Referenced once, in the merge commit only',
              detail: 'A single mention; the rule scores it lower than a spread of commits.',
              sourceSystem: 'Work Tracker',
              observedAt: '2026-01-27T19:30:00.000Z',
            },
          ],
          'One mention in a merge commit is consistent with the item having been in the release and with it having been mentioned in passing.',
        ),
      ),
    ],
    unlinkedWorkItemCount: 17,
    claim: {
      claimedSharePercent: null,
      shareUnavailableReason:
        'The business case forecast a reduction to 4.2 per thousand but never expressed it as a share of the outcome, and none has been derived — deriving one would mean inventing a denominator.',
      statement:
        'The FY26 business case forecast that transfer rescanning would take mishandled bags from 5.0 to 4.2 per thousand passengers within two quarters.',
      claimedBy: 'Tomas Lindqvist (Product Owner, Baggage Engineering)',
      claimedAt: '2025-11-14T15:00:00.000Z',
      basis: 'BusinessCaseForecast',
      corroborated: false,
      corroborationNote:
        'Nobody has checked the forecast against an outturn, and under the current definition change nobody can.',
    },
    claimAbsentReason: null,
    verdict: {
      state: 'Refused',
      headline: 'Metric redefined mid-window',
      statement:
        'The delivery link here is one of the strongest on the screen: declared in advance, corroborated by twelve commits. It makes no difference. The outcome metric changed definition on 12 March 2026, inside the window, so there is no movement to attribute the release to.',
      refusalReason:
        'MET-214 was restated from "per thousand passengers" (v3.2) to "per thousand bags carried" (v4.0) on 12 March 2026. Subtracting a v4.0 figure from a v3.2 figure produces a number, and that number is a unit conversion rather than an improvement. No attribution is computed and none should be quoted from this screen until the outcome owner republishes a v4.0 baseline.',
      confidence: 'Unknown',
    },
  },
];

const CHAINS_BOOK: OutcomeTraceChain[] = [
  {
    id: uuidFrom('chain:book:R26.04-12'),
    outcome: OUT_BOOK,
    release: release('R26.04-12', 'One-page checkout', BOOK, '2026-04-08', '2026-04-09', 24),
    releaseLink: declared(
      'Ines Okafor (Director Digital Commerce)',
      '2026-04-10T07:55:00.000Z',
      [
        {
          label: 'Release note names OUT-BOOK-01',
          detail: 'Product Registry field `targetOutcome` = OUT-BOOK-01.',
          sourceSystem: 'Product Registry',
          observedAt: '2026-04-02T09:15:00.000Z',
        },
        {
          label: 'Funnel instrumentation shipped with the release',
          detail:
            'Step-completion events added for all four checkout steps, so the funnel can be read directly rather than inferred from the outcome metric.',
          sourceSystem: 'Aurora Data Hub',
          observedAt: '2026-04-09T18:00:00.000Z',
        },
      ],
      'The release shipped on 9 April, three days into a seven-day fare sale. No holdout group was kept, so the release and the sale cannot be separated in MET-217.',
    ),
    workItems: [
      traced(
        workItem(
          'AUR-15702',
          'Collapse passenger details, seat and payment into one page',
          'Feature',
          'Shopping & Offer',
          '2026-04-03',
          55,
        ),
        inferredFromReference(
          'rule:commit-key-scan@v2',
          '2026-04-09T18:20:00.000Z',
          0.97,
          [
            {
              label: 'Twenty-one commits carry the key',
              detail: 'AUR-15702, R26.04-12.',
              sourceSystem: 'Work Tracker',
              observedAt: '2026-04-09T18:20:00.000Z',
            },
          ],
          null,
        ),
      ),
      traced(
        workItem(
          'AUR-15744',
          'Keep the fare quote alive for 20 minutes during checkout',
          'Enhancement',
          'Order & Servicing',
          '2026-04-07',
          21,
        ),
        declared(
          'Ines Okafor (Director Digital Commerce)',
          '2026-04-10T07:55:00.000Z',
          [
            {
              label: 'Named in the release note as a dependency of the one-page flow',
              detail: 'Cross-team item; no shared commit history with the checkout branch.',
              sourceSystem: 'Product Registry',
              observedAt: '2026-04-10T07:55:00.000Z',
            },
          ],
          null,
        ),
      ),
    ],
    unlinkedWorkItemCount: 22,
    claim: {
      claimedSharePercent: 0.6,
      shareUnavailableReason: null,
      statement:
        'Digital Commerce claims 60% of the conversion lift for one-page checkout, on the basis that step-completion in the checkout funnel rose 14 points while top-of-funnel traffic was flat.',
      claimedBy: 'Ines Okafor (Director Digital Commerce)',
      claimedAt: '2026-05-06T13:30:00.000Z',
      basis: 'TeamAssertion',
      corroborated: false,
      corroborationNote:
        'The funnel evidence is real and specific, which is more than most claims have. It still does not separate the release from the fare sale that overlapped it, and no holdout was kept.',
    },
    claimAbsentReason: null,
    verdict: {
      state: 'Consistent',
      headline: 'Consistent, not established',
      statement:
        'The metric moved, the link was declared in advance, and the funnel evidence points at the checkout step specifically rather than at the outcome in general. Three confounders remain live, one of them — a fare sale overlapping the release date — large enough to produce the whole movement on its own. The right reading is that the release is consistent with the lift, not that it caused it.',
      refusalReason: null,
      confidence: 'Medium',
    },
  },
];

const CHAINS_FUEL: OutcomeTraceChain[] = [
  {
    id: uuidFrom('chain:fuel:R26.02-11'),
    outcome: OUT_FUEL,
    release: release(
      'R26.02-11',
      'Continuous descent advisory',
      FUEL,
      '2026-02-20',
      '2026-02-24',
      9,
    ),
    releaseLink: declared(
      'Dr Sanne Broekhuis (Head of Flight Efficiency)',
      '2026-02-25T10:05:00.000Z',
      [
        {
          label: 'Release note names OUT-FUEL-01',
          detail: 'Product Registry field `targetOutcome` = OUT-FUEL-01.',
          sourceSystem: 'Product Registry',
          observedAt: '2026-02-18T11:40:00.000Z',
        },
      ],
      'The link is declared and credible. It points at a metric that has not been computed since February.',
    ),
    workItems: [
      traced(
        workItem(
          'AUR-14977',
          'Advise a continuous descent profile where ATC constraints allow',
          'Feature',
          'Fuel & Efficiency Analytics',
          '2026-02-17',
          34,
        ),
        inferredFromReference(
          'rule:commit-key-scan@v2',
          '2026-02-24T21:00:00.000Z',
          0.93,
          [
            {
              label: 'Eleven commits carry the key',
              detail: 'AUR-14977, R26.02-11.',
              sourceSystem: 'Work Tracker',
              observedAt: '2026-02-24T21:00:00.000Z',
            },
          ],
          null,
        ),
      ),
    ],
    unlinkedWorkItemCount: 8,
    claim: {
      claimedSharePercent: null,
      shareUnavailableReason:
        'The vendor quoted an absolute saving, not a share of the outcome. No share has been derived from it.',
      statement:
        'The avionics vendor estimates €4.1m of annualised fuel saving from continuous descent advisories across our fleet and route mix.',
      claimedBy: 'Dr Sanne Broekhuis (Head of Flight Efficiency), quoting the vendor',
      claimedAt: '2026-03-11T09:00:00.000Z',
      basis: 'VendorEstimate',
      corroborated: false,
      corroborationNote:
        'The estimate is the vendor’s own, produced from their model on our schedule file. Nothing in Aurora has verified it, and the feed that would has been down since 9 February.',
    },
    claimAbsentReason: null,
    verdict: {
      state: 'ClaimOnly',
      headline: 'Claimed, never measured',
      statement:
        'A vendor estimate of €4.1m is the only figure attached to this outcome. The instrument that would test it — the ACARS fuel feed — has not delivered since 9 February 2026, so three of the five months in the window have no value at all. The €4.1m is a forecast and this screen will keep calling it one until MET-221 has a current figure.',
      refusalReason: null,
      confidence: 'Low',
    },
  },
];

const CHAINS_DSCM: OutcomeTraceChain[] = [
  {
    id: uuidFrom('chain:dscm:R26.03-02'),
    outcome: OUT_DSCM,
    release: release(
      'R26.03-02',
      'Channel preference honoured on disruption',
      DSCM,
      '2026-03-05',
      '2026-03-06',
      16,
    ),
    releaseLink: declared(
      'Ines Okafor (Director Digital Commerce)',
      '2026-03-07T09:30:00.000Z',
      [
        {
          label: 'Release note names OUT-DSCM-01, with a stated threshold',
          detail: 'Pre-registered: "proactive notification rate above 70% within one quarter".',
          sourceSystem: 'Product Registry',
          observedAt: '2026-03-02T16:20:00.000Z',
        },
      ],
      null,
    ),
    workItems: [
      traced(
        workItem(
          'AUR-15190',
          'Send disruption notices on the passenger’s chosen channel, not e-mail by default',
          'Feature',
          'Customer Care Platform',
          '2026-03-03',
          21,
        ),
        inferredFromReference(
          'rule:commit-key-scan@v2',
          '2026-03-06T20:00:00.000Z',
          0.9,
          [
            {
              label: 'Seven commits carry the key',
              detail: 'AUR-15190, R26.03-02.',
              sourceSystem: 'Work Tracker',
              observedAt: '2026-03-06T20:00:00.000Z',
            },
          ],
          null,
        ),
      ),
      traced(
        workItem(
          'AUR-15233',
          'Back-fill channel preference for members who never set one',
          'Operational',
          'Customer Care Platform',
          '2026-03-04',
          5,
        ),
        inferredFromReference(
          'rule:commit-key-scan@v2',
          '2026-03-06T20:00:00.000Z',
          0.86,
          [
            {
              label: 'Referenced in the release manifest',
              detail: 'AUR-15233, R26.03-02.',
              sourceSystem: 'Work Tracker',
              observedAt: '2026-03-06T20:00:00.000Z',
            },
          ],
          null,
        ),
      ),
    ],
    unlinkedWorkItemCount: 14,
    claim: {
      claimedSharePercent: null,
      shareUnavailableReason:
        'No share was claimed. The team registered a threshold instead, which the metric did not reach.',
      statement:
        'The team expected the proactive notification rate to pass 70% within one quarter of honouring channel preference.',
      claimedBy: 'Ines Okafor (Director Digital Commerce)',
      claimedAt: '2026-03-02T16:20:00.000Z',
      basis: 'TeamAssertion',
      corroborated: false,
      corroborationNote: null,
    },
    claimAbsentReason: null,
    verdict: {
      state: 'NoMovement',
      headline: 'Shipped; metric did not move',
      statement:
        'The release shipped on 6 March against a pre-registered threshold of 70%. Eleven weeks later MET-224 stands at 64%, one point above the FY25 Q4 baseline and inside the month-to-month noise of the last nine months. The honest reading is that this delivery has not moved this outcome. That is a finding about the theory, not about the team, and it is reported here at the same weight a success would be.',
      refusalReason: null,
      confidence: 'High',
    },
  },
];

// ── Round 6: movements and chains for the ten added above ────────────────────

/**
 * A movement built from its own series.
 *
 * The seven originals spell out `currentValue`, `deltaAbsolute`, `deltaPercent`
 * and `direction` by hand, which is fine for seven and a liability for
 * seventeen — the delta and the last point of the series are two statements of
 * one fact, and hand-copying them is how they come to disagree. Here the series
 * is the single source and the rest is read off it.
 */
function movementFrom(options: {
  metricId: string;
  metricLabel: string;
  unit: MetricUnit;
  points: Array<[string, number]>;
  baselineLabel: string;
  baselineValue: number;
  target: number | null;
  targetUnavailableReason?: string | null;
  desiredDirection: 'Up' | 'Down';
  confidence?: 'High' | 'Medium' | 'Low';
  qualityScore?: number;
  confounders: OutcomeMovement['confounders'];
}): OutcomeMovement {
  const last = options.points[options.points.length - 1];
  if (!last) throw new Error(`Movement ${options.metricId} has an empty series.`);
  const currentValue = last[1];
  const round = (value: number) => Number(value.toFixed(4));
  const deltaAbsolute = round(currentValue - options.baselineValue);
  const deltaPercent =
    options.baselineValue === 0 ? null : round(deltaAbsolute / options.baselineValue);
  const better =
    options.desiredDirection === 'Up' ? deltaAbsolute > 0 : deltaAbsolute < 0;
  const direction: OutcomeMovement['direction'] =
    deltaAbsolute === 0 ? 'Flat' : better ? 'Improved' : 'Worsened';

  return {
    metric: metric(options.metricId, options.metricLabel, currentValue, options.unit, {
      desiredDirection: options.desiredDirection,
      comparisonValue: options.baselineValue,
      comparisonLabel: options.baselineLabel,
      confidence: options.confidence ?? 'Medium',
      qualityScore: options.qualityScore ?? 74,
    }),
    baselineLabel: options.baselineLabel,
    baselineValue: options.baselineValue,
    baselineUnavailableReason: null,
    currentValue,
    deltaAbsolute,
    deltaPercent,
    direction,
    target: options.target,
    targetUnavailableReason: options.targetUnavailableReason ?? null,
    measurable: true,
    unmeasurableReason: null,
    definitionVersionAtBaseline: 'v4.0',
    definitionVersionNow: 'v4.0',
    definitionChangedAt: null,
    definitionChangeNote: null,
    series: series(options.points, 'v4.0'),
    confounders: options.confounders,
  };
}

/**
 * One release, two work items, a claim and a verdict.
 *
 * The release link is always declared and the second work item is always
 * inferred from a commit key, which is the ordinary shape: a human names the
 * outcome, a rule finds the code. It keeps every added outcome honest about
 * which half of its evidence a person actually asserted.
 */
function deliveredChain(options: {
  key: string;
  outcome: ProductOutcomeRef;
  productRef: ReturnType<typeof product>;
  releaseCode: string;
  releaseName: string;
  plannedDate: string;
  actualDate: string;
  workItemCount: number;
  unlinkedWorkItemCount: number;
  declaredBy: string;
  declaredAt: string;
  registryObservedAt: string;
  evidenceDetail: string;
  releaseCaveat: string;
  declaredItem: { reference: string; title: string; team: string; closedAt: string; effort: number };
  inferredItem: {
    reference: string;
    title: string;
    team: string;
    closedAt: string;
    effort: number | null;
    effortUnavailableReason?: string;
    score: number;
    commitDetail: string;
  };
  claimedSharePercent: number;
  claimStatement: string;
  claimedAt: string;
  corroborated: boolean;
  corroborationNote: string;
  verdictState: AttributionState;
  verdictHeadline: string;
  verdictStatement: string;
  verdictConfidence: 'High' | 'Medium' | 'Low';
}): OutcomeTraceChain {
  return {
    id: uuidFrom(`chain:${options.key}:${options.releaseCode}`),
    outcome: options.outcome,
    release: release(
      options.releaseCode,
      options.releaseName,
      options.productRef,
      options.plannedDate,
      options.actualDate,
      options.workItemCount,
    ),
    releaseLink: declared(
      options.declaredBy,
      options.declaredAt,
      [
        {
          label: `Release note names ${options.outcome.code} as the target outcome`,
          detail: options.evidenceDetail,
          sourceSystem: 'Product Registry',
          observedAt: options.registryObservedAt,
        },
      ],
      options.releaseCaveat,
    ),
    workItems: [
      traced(
        workItem(
          options.declaredItem.reference,
          options.declaredItem.title,
          'Feature',
          options.declaredItem.team,
          options.declaredItem.closedAt,
          options.declaredItem.effort,
        ),
        declared(
          options.declaredBy,
          options.declaredAt,
          [
            {
              label: 'Named in the release by the owner',
              detail: `Listed under "included work" in the ${options.releaseCode} manifest and signed by the owner.`,
              sourceSystem: 'Product Registry',
              observedAt: options.registryObservedAt,
            },
          ],
          null,
        ),
      ),
      traced(
        workItem(
          options.inferredItem.reference,
          options.inferredItem.title,
          'Enhancement',
          options.inferredItem.team,
          options.inferredItem.closedAt,
          options.inferredItem.effort,
          options.inferredItem.effortUnavailableReason ?? null,
        ),
        inferredFromReference(
          'rule:commit-key-scan@v2',
          options.declaredAt,
          options.inferredItem.score,
          [
            {
              label: 'Carries the work key on the release branch',
              detail: options.inferredItem.commitDetail,
              sourceSystem: 'Work Tracker',
              observedAt: options.declaredAt,
            },
          ],
          null,
        ),
      ),
    ],
    unlinkedWorkItemCount: options.unlinkedWorkItemCount,
    claim: {
      claimedSharePercent: options.claimedSharePercent,
      shareUnavailableReason: null,
      statement: options.claimStatement,
      claimedBy: options.declaredBy,
      claimedAt: options.claimedAt,
      basis: 'TeamAssertion',
      corroborated: options.corroborated,
      corroborationNote: options.corroborationNote,
    },
    claimAbsentReason: null,
    verdict: {
      state: options.verdictState,
      headline: options.verdictHeadline,
      statement: options.verdictStatement,
      refusalReason: null,
      confidence: options.verdictConfidence,
    },
  };
}

const MOVEMENT_MRO = movementFrom({
  metricId: 'MET-241',
  metricLabel: 'AOG hours per 1,000 flight hours',
  unit: 'hours',
  points: [
    ['2025-09', 4.9],
    ['2025-10', 4.8],
    ['2025-11', 5.1],
    ['2025-12', 4.7],
    ['2026-01', 4.4],
    ['2026-02', 4.0],
    ['2026-03', 3.6],
    ['2026-04', 3.4],
    ['2026-05', 3.3],
  ],
  baselineLabel: 'FY25 Q4 average (Oct–Dec 2025)',
  baselineValue: 4.9,
  target: 3.0,
  desiredDirection: 'Down',
  confidence: 'High',
  qualityScore: 84,
  confounders: [
    {
      label: 'Two A320s left the fleet in February',
      detail:
        'Both were the oldest airframes at Chicago and between them accounted for a fifth of FY25 AOG hours. Retiring them lowers the rate without anything being predicted better.',
      occurredAt: '2026-02-06',
    },
  ],
});

const MOVEMENT_FPD = movementFrom({
  metricId: 'MET-244',
  metricLabel: 'Enroute re-plan rate',
  unit: 'percent',
  points: [
    ['2025-09', 0.118],
    ['2025-10', 0.115],
    ['2025-11', 0.121],
    ['2025-12', 0.116],
    ['2026-01', 0.109],
    ['2026-02', 0.104],
    ['2026-03', 0.098],
    ['2026-04', 0.094],
    ['2026-05', 0.091],
  ],
  baselineLabel: 'FY25 Q4 average (Oct–Dec 2025)',
  baselineValue: 0.117,
  target: 0.085,
  desiredDirection: 'Down',
  confidence: 'Medium',
  qualityScore: 76,
  confounders: [
    {
      label: 'A quieter winter over the North Atlantic',
      detail:
        'Nineteen fewer days of significant jet-stream deviation than FY25. Fewer re-plans follow from the weather alone and no attempt has been made to normalise for it.',
      occurredAt: '2026-03-01',
    },
  ],
});

// In US cents, not dollars. At 0.0871 dollars the tile rendered "0.087" — a
// bare fraction with no unit on it, and a target of "0.089" that looked like
// the same number.
const MOVEMENT_PRM = movementFrom({
  metricId: 'MET-247',
  metricLabel: 'Revenue per available seat km (US cents)',
  unit: 'ratio',
  points: [
    ['2025-09', 8.12],
    ['2025-10', 8.18],
    ['2025-11', 8.09],
    ['2025-12', 8.34],
    ['2026-01', 8.41],
    ['2026-02', 8.47],
    ['2026-03', 8.59],
    ['2026-04', 8.64],
    ['2026-05', 8.71],
  ],
  baselineLabel: 'FY25 Q4 average (Oct–Dec 2025)',
  baselineValue: 8.2,
  target: 8.9,
  desiredDirection: 'Up',
  confidence: 'Medium',
  qualityScore: 71,
  confounders: [
    {
      label: 'A competitor withdrew from three base markets in January',
      detail:
        'The same withdrawal Commercial credits for the Skyward movement. On these routes Aurora is close to the only carrier, and fares rose without any pricing change of ours.',
      occurredAt: '2026-01-15',
    },
    {
      label: 'Jet fuel fell 11% across the window',
      detail:
        'RASK is a revenue measure and is not mechanically moved by fuel, but the network team re-timed four thin routes it would otherwise have cut. Assessed as material by Revenue Management.',
      occurredAt: '2026-02-20',
    },
  ],
});

const MOVEMENT_CCC = movementFrom({
  metricId: 'MET-251',
  metricLabel: 'Temperature excursion rate',
  unit: 'percent',
  points: [
    ['2025-09', 0.031],
    ['2025-10', 0.029],
    ['2025-11', 0.03],
    ['2025-12', 0.027],
    ['2026-01', 0.024],
    ['2026-02', 0.021],
    ['2026-03', 0.018],
    ['2026-04', 0.016],
    ['2026-05', 0.015],
  ],
  baselineLabel: 'FY25 Q4 average (Oct–Dec 2025)',
  baselineValue: 0.029,
  target: 0.01,
  desiredDirection: 'Down',
  confidence: 'High',
  qualityScore: 86,
  confounders: [
    {
      label: 'Delhi ramp cold storage came into service in November',
      detail:
        'A capital project with its own business case, delivered by Ground Operations rather than by this product. Delhi carries a third of pharma tonnage and its excursions fell fastest.',
      occurredAt: '2025-11-24',
    },
  ],
});

const MOVEMENT_ANC = movementFrom({
  metricId: 'MET-254',
  metricLabel: 'Ancillary attach rate',
  unit: 'percent',
  points: [
    ['2025-09', 0.284],
    ['2025-10', 0.288],
    ['2025-11', 0.291],
    ['2025-12', 0.297],
    ['2026-01', 0.304],
    ['2026-02', 0.309],
    ['2026-03', 0.318],
    ['2026-04', 0.324],
    ['2026-05', 0.331],
  ],
  baselineLabel: 'FY25 Q4 average (Oct–Dec 2025)',
  baselineValue: 0.292,
  target: 0.35,
  desiredDirection: 'Up',
  confidence: 'Medium',
  qualityScore: 73,
  confounders: [
    {
      label: 'Seat selection moved earlier in the booking flow in February',
      detail:
        'A checkout change owned by Flight Booking, not by this product. It lifts attach rate mechanically by asking sooner, and the refund rate on seats rose 0.4pp in the same window.',
      occurredAt: '2026-02-11',
    },
  ],
});

// Expressed as the share of passengers boarding inside four minutes rather
// than as a median duration. MetricUnit has no minutes, and the nearest
// available unit rendered 4.5 minutes as "4.5" beside a target of "4" — a
// figure a reader would take for days.
const MOVEMENT_CKI = movementFrom({
  metricId: 'MET-257',
  metricLabel: 'Passengers boarding within four minutes',
  unit: 'percent',
  points: [
    ['2025-09', 0.62],
    ['2025-10', 0.635],
    ['2025-11', 0.611],
    ['2025-12', 0.652],
    ['2026-01', 0.698],
    ['2026-02', 0.735],
    ['2026-03', 0.781],
    ['2026-04', 0.818],
    ['2026-05', 0.839],
  ],
  baselineLabel: 'FY25 Q4 average (Oct–Dec 2025)',
  baselineValue: 0.633,
  target: 0.9,
  desiredDirection: 'Up',
  confidence: 'Medium',
  qualityScore: 69,
  confounders: [
    {
      label: 'Load factor fell 1.8pp across the window',
      detail:
        'Fewer passengers per departure shortens every queue. Ground Operations judged the effect material and it has not been separated from the boarding-group change.',
      occurredAt: '2026-03-31',
    },
  ],
});

const MOVEMENT_CARE = movementFrom({
  metricId: 'MET-261',
  metricLabel: 'First-contact resolution rate',
  unit: 'percent',
  points: [
    ['2025-09', 0.671],
    ['2025-10', 0.679],
    ['2025-11', 0.668],
    ['2025-12', 0.684],
    ['2026-01', 0.702],
    ['2026-02', 0.716],
    ['2026-03', 0.728],
    ['2026-04', 0.741],
    ['2026-05', 0.749],
  ],
  baselineLabel: 'FY25 Q4 average (Oct–Dec 2025)',
  baselineValue: 0.677,
  target: 0.8,
  desiredDirection: 'Up',
  confidence: 'High',
  qualityScore: 81,
  confounders: [
    {
      label: 'Disruption volume was lower than FY25',
      detail:
        'Fewer disrupted passengers means fewer of the multi-contact rebooking cases that drag the rate down. Customer Care assessed this as material and did not normalise for it.',
      occurredAt: '2026-02-28',
    },
  ],
});

const MOVEMENT_SMS = movementFrom({
  metricId: 'MET-264',
  metricLabel: 'Median days to close a Level 1 finding',
  unit: 'days',
  points: [
    ['2025-09', 47],
    ['2025-10', 46],
    ['2025-11', 48],
    ['2025-12', 44],
    ['2026-01', 41],
    ['2026-02', 39],
    ['2026-03', 36],
    ['2026-04', 34],
    ['2026-05', 33],
  ],
  baselineLabel: 'FY25 Q4 average (Oct–Dec 2025)',
  baselineValue: 46,
  target: 30,
  desiredDirection: 'Down',
  confidence: 'High',
  qualityScore: 88,
  confounders: [],
});

// Grams per RPK, not kilograms. In kilograms the tile showed the current
// figure and the target as the same rounded "0.08", which made a live
// commitment look like it had already been met.
const MOVEMENT_SUS = movementFrom({
  metricId: 'MET-267',
  metricLabel: 'CO2 grams per revenue passenger km',
  unit: 'ratio',
  points: [
    ['2025-09', 89.1],
    ['2025-10', 88.6],
    ['2025-11', 89.4],
    ['2025-12', 87.8],
    ['2026-01', 86.9],
    ['2026-02', 86.3],
    ['2026-03', 85.7],
    ['2026-04', 85.2],
    ['2026-05', 84.9],
  ],
  baselineLabel: 'FY25 full year',
  baselineValue: 88.7,
  target: 82.0,
  desiredDirection: 'Down',
  confidence: 'Medium',
  qualityScore: 72,
  confounders: [
    {
      label: 'Load factor and fleet mix both moved inside the window',
      detail:
        'CO2 per RPK falls when aircraft are fuller and when newer airframes fly more. Both happened. The outcome commits to constant network and constant fleet mix, and this series is not yet normalised for either.',
      occurredAt: '2026-04-30',
    },
  ],
});

const MOVEMENT_NET = movementFrom({
  metricId: 'MET-271',
  metricLabel: 'Schedule completion factor',
  unit: 'percent',
  points: [
    ['2025-09', 0.9862],
    ['2025-10', 0.9871],
    ['2025-11', 0.9848],
    ['2025-12', 0.9819],
    ['2026-01', 0.9857],
    ['2026-02', 0.9874],
    ['2026-03', 0.9889],
    ['2026-04', 0.9901],
    ['2026-05', 0.9908],
  ],
  baselineLabel: 'FY25 Q4 average (Oct–Dec 2025)',
  baselineValue: 0.9846,
  target: 0.993,
  desiredDirection: 'Up',
  confidence: 'Medium',
  qualityScore: 77,
  confounders: [
    {
      label: 'December was the worst weather month in five years',
      detail:
        'The baseline quarter contains it, which flatters every comparison drawn against that quarter. Network Planning has said so rather than rebasing.',
      occurredAt: '2025-12-22',
    },
  ],
});

const CHAINS_MRO: OutcomeTraceChain[] = [
  deliveredChain({
    key: 'mro',
    outcome: OUT_MRO,
    productRef: MRO,
    releaseCode: 'R26.01-07',
    releaseName: 'Predictive spares positioning at the four bases',
    plannedDate: '2026-01-16',
    actualDate: '2026-01-21',
    workItemCount: 24,
    unlinkedWorkItemCount: 19,
    declaredBy: 'Ivan Petrov (Head of Maintenance Planning)',
    declaredAt: '2026-01-22T08:40:00.000Z',
    registryObservedAt: '2026-01-14T10:05:00.000Z',
    evidenceDetail:
      'Product Registry field `targetOutcome` = OUT-MRO-01, set a week before the release shipped.',
    releaseCaveat:
      'Declared before the release shipped. It does not separate the model from the two airframes that left the fleet three weeks later.',
    declaredItem: {
      reference: 'AUR-15402',
      title: 'Forecast rotable demand per base from removal history',
      team: 'Maintenance Systems',
      closedAt: '2026-01-15',
      effort: 21,
    },
    inferredItem: {
      reference: 'AUR-15455',
      title: 'Flag a spare as at risk before the stock-out, not after',
      team: 'Maintenance Systems',
      closedAt: '2026-01-19',
      effort: 8,
      score: 0.91,
      commitDetail: 'AUR-15455 appears in 6 of 24 commits in R26.01-07.',
    },
    claimedSharePercent: 0.55,
    claimStatement:
      'Maintenance Planning claims a little over half the improvement, on the grounds that AOG hours began falling in the month the model shipped and the two retirements did not land until February.',
    claimedAt: '2026-04-08T10:15:00.000Z',
    corroborated: true,
    corroborationNote:
      'Engineering accepted the direction and disputed the size, attributing more of it to the retirements. The 55% is the team’s figure, not an agreed one.',
    verdictState: 'Measured',
    verdictHeadline: 'Measured movement, strong link',
    verdictStatement:
      'The link was declared before the release, the metric moved in the month it shipped, and the movement held for four more. One confounder — two retired airframes carrying a fifth of FY25 AOG hours — is material and unquantified, so the direction is safe and the 55% share is not.',
    verdictConfidence: 'High',
  }),
];

const CHAINS_FPD: OutcomeTraceChain[] = [
  deliveredChain({
    key: 'fpd',
    outcome: OUT_FPD,
    productRef: FPD,
    releaseCode: 'R26.02-11',
    releaseName: 'Wind model refresh every six hours instead of twelve',
    plannedDate: '2026-02-05',
    actualDate: '2026-02-09',
    workItemCount: 17,
    unlinkedWorkItemCount: 13,
    declaredBy: 'Dele Okonjo (Head of Flight Operations)',
    declaredAt: '2026-02-10T07:25:00.000Z',
    registryObservedAt: '2026-02-03T16:20:00.000Z',
    evidenceDetail:
      'Product Registry field `targetOutcome` = OUT-FPD-01, recorded with a stated threshold of "re-plan rate under 10% within 90 days".',
    releaseCaveat:
      'Pre-registered with a threshold, which rules out picking the outcome after seeing the number. It does not rule out the weather.',
    declaredItem: {
      reference: 'AUR-15511',
      title: 'Ingest the 6-hourly GFS cycle into the flight planning engine',
      team: 'Dispatch Platform',
      closedAt: '2026-02-04',
      effort: 13,
    },
    inferredItem: {
      reference: 'AUR-15540',
      title: 'Re-cost the filed route when the wind aloft moves more than 15 knots',
      team: 'Dispatch Platform',
      closedAt: '2026-02-07',
      effort: 5,
      score: 0.87,
      commitDetail: 'AUR-15540 referenced in the R26.02-11 manifest and in 4 commits.',
    },
    claimedSharePercent: 0.4,
    claimStatement:
      'Dispatch Platform claims about 40%, deliberately conservative: the quieter North Atlantic winter is the obvious rival explanation and the team says so first.',
    claimedAt: '2026-04-15T13:00:00.000Z',
    corroborated: false,
    corroborationNote:
      'Not yet reviewed by Flight Operations. Recorded as the team’s own claim until it is.',
    verdictState: 'Consistent',
    verdictHeadline: 'Consistent, not isolated',
    verdictStatement:
      'The rate crossed the pre-registered threshold and the link was declared in advance. A materially quieter winter over the North Atlantic covers the same window, so the release is consistent with the movement rather than shown to have caused it.',
    verdictConfidence: 'Medium',
  }),
];

const CHAINS_PRM: OutcomeTraceChain[] = [
  deliveredChain({
    key: 'prm',
    outcome: OUT_PRM,
    productRef: PRM,
    releaseCode: 'R26.03-02',
    releaseName: 'Continuous pricing on the twelve densest routes',
    plannedDate: '2026-03-04',
    actualDate: '2026-03-11',
    workItemCount: 29,
    unlinkedWorkItemCount: 24,
    declaredBy: 'Marcus Lindqvist (Director Revenue Management)',
    declaredAt: '2026-03-12T09:00:00.000Z',
    registryObservedAt: '2026-03-02T11:40:00.000Z',
    evidenceDetail: 'Product Registry field `targetOutcome` = OUT-PRM-01, set before the release.',
    releaseCaveat:
      'A competitor left three base markets in January, before this shipped. Any RASK claim has to survive that first, and this one has not been separated from it.',
    declaredItem: {
      reference: 'AUR-15602',
      title: 'Replace the 26 fare buckets with a continuous price on trunk routes',
      team: 'Revenue Systems',
      closedAt: '2026-03-06',
      effort: 34,
    },
    inferredItem: {
      reference: 'AUR-15644',
      title: 'Re-fit the willingness-to-pay curve nightly rather than weekly',
      team: 'Revenue Systems',
      closedAt: '2026-03-09',
      effort: null,
      effortUnavailableReason:
        'Revenue Systems stopped estimating in October 2025. Effort is unknown for their items, not zero.',
      score: 0.83,
      commitDetail: 'AUR-15644 appears in 7 of 29 commits in R26.03-02.',
    },
    claimedSharePercent: 0.3,
    claimStatement:
      'Revenue Management claims under a third, and states plainly that the January market withdrawal is the larger share of the movement.',
    claimedAt: '2026-05-06T15:30:00.000Z',
    corroborated: true,
    corroborationNote:
      'Finance reviewed on 12 May and accepted the 30% as the most defensible split available, while noting that no counterfactual route set was held back.',
    verdictState: 'ClaimOnly',
    verdictHeadline: 'Claim only, movement shared',
    verdictStatement:
      'RASK rose and the release is credibly part of it, but the competitor withdrawal is the dominant confounder and no control routes were held back. What exists is a reviewed claim, not a measurement of this release’s contribution.',
    verdictConfidence: 'Low',
  }),
];

const CHAINS_CCC: OutcomeTraceChain[] = [
  deliveredChain({
    key: 'ccc',
    outcome: OUT_CCC,
    productRef: CCC,
    releaseCode: 'R25.12-05',
    releaseName: 'Live container telemetry on the pharma lanes',
    plannedDate: '2025-12-03',
    actualDate: '2025-12-09',
    workItemCount: 21,
    unlinkedWorkItemCount: 16,
    declaredBy: 'Nora Sadiq (Head of Cargo)',
    declaredAt: '2025-12-10T11:15:00.000Z',
    registryObservedAt: '2025-12-01T09:30:00.000Z',
    evidenceDetail: 'Product Registry field `targetOutcome` = OUT-CCC-01, set before the release.',
    releaseCaveat:
      'Delhi ramp cold storage opened two weeks earlier and carries a third of pharma tonnage. The two changes are not separable in this series.',
    declaredItem: {
      reference: 'AUR-15288',
      title: 'Stream container probe readings into the cargo record every 15 minutes',
      team: 'Cargo Platform',
      closedAt: '2025-12-02',
      effort: 21,
    },
    inferredItem: {
      reference: 'AUR-15310',
      title: 'Alert the ramp when a container drifts within 1°C of its limit',
      team: 'Cargo Platform',
      closedAt: '2025-12-06',
      effort: 13,
      score: 0.94,
      commitDetail: 'AUR-15310 appears in 9 of 21 commits in R25.12-05.',
    },
    claimedSharePercent: 0.6,
    claimStatement:
      'Cargo claims 60%, on the grounds that excursions fell on lanes without new cold storage as well as on those with it.',
    claimedAt: '2026-03-19T14:45:00.000Z',
    corroborated: true,
    corroborationNote:
      'Ground Operations reviewed on 27 March and agreed, having compared Delhi against Houston where no storage was built and the rate fell nearly as far.',
    verdictState: 'Measured',
    verdictHeadline: 'Measured movement, strong link',
    verdictStatement:
      'The link was declared before the release and the improvement appears on lanes that got no new cold storage, which is the closest thing to a control in this dataset. The excursion rate halved and the alerting is the best available explanation for most of it.',
    verdictConfidence: 'High',
  }),
];

const CHAINS_ANC: OutcomeTraceChain[] = [
  deliveredChain({
    key: 'anc',
    outcome: OUT_ANC,
    productRef: ANC,
    releaseCode: 'R26.01-14',
    releaseName: 'Bundle recommendations from prior trips',
    plannedDate: '2026-01-22',
    actualDate: '2026-01-28',
    workItemCount: 18,
    unlinkedWorkItemCount: 14,
    declaredBy: 'Ines Okafor (Director Digital Commerce)',
    declaredAt: '2026-01-29T10:10:00.000Z',
    registryObservedAt: '2026-01-20T13:15:00.000Z',
    evidenceDetail: 'Product Registry field `targetOutcome` = OUT-ANC-01, set before the release.',
    releaseCaveat:
      'Flight Booking moved seat selection earlier in the flow two weeks later, which raises attach rate on its own. The outcome commits to a flat refund rate and refunds on seats rose 0.4pp.',
    declaredItem: {
      reference: 'AUR-15470',
      title: 'Recommend the bundle a traveller bought on their last two trips',
      team: 'Shopping & Offer',
      closedAt: '2026-01-21',
      effort: 13,
    },
    inferredItem: {
      reference: 'AUR-15498',
      title: 'Suppress the offer where the fare already includes it',
      team: 'Shopping & Offer',
      closedAt: '2026-01-26',
      effort: 5,
      score: 0.88,
      commitDetail: 'AUR-15498 referenced in 5 commits on the R26.01-14 branch.',
    },
    claimedSharePercent: 0.45,
    claimStatement:
      'Digital Commerce claims 45%, and flags that the rise in seat refunds may mean part of the attach growth is not the relevance the outcome asks for.',
    claimedAt: '2026-04-22T09:20:00.000Z',
    corroborated: false,
    corroborationNote:
      'The Value Steward has asked for the refund rate to be split by ancillary type before accepting the claim. Outstanding.',
    verdictState: 'Consistent',
    verdictHeadline: 'Consistent, quality unresolved',
    verdictStatement:
      'Attach rate rose steadily from the release onward. The outcome asks for growth from relevance and holds refunds flat as the test of it; refunds rose, and until they are split by type this reads as consistent with the release rather than as the outcome being met.',
    verdictConfidence: 'Medium',
  }),
];

const CHAINS_CKI: OutcomeTraceChain[] = [
  deliveredChain({
    key: 'cki',
    outcome: OUT_CKI,
    productRef: CKI,
    releaseCode: 'R26.02-18',
    releaseName: 'Boarding groups by cabin position rather than tier',
    plannedDate: '2026-02-19',
    actualDate: '2026-02-25',
    workItemCount: 15,
    unlinkedWorkItemCount: 11,
    declaredBy: 'Peter Vaardt (Head of Ground Operations)',
    declaredAt: '2026-02-26T06:50:00.000Z',
    registryObservedAt: '2026-02-17T15:00:00.000Z',
    evidenceDetail: 'Product Registry field `targetOutcome` = OUT-CKI-01, set before the release.',
    releaseCaveat:
      'Load factor fell 1.8pp over the same window, which shortens every queue regardless of boarding order.',
    declaredItem: {
      reference: 'AUR-15577',
      title: 'Assign boarding groups back-to-front by seat row within tier',
      team: 'Airport Platform',
      closedAt: '2026-02-18',
      effort: 8,
    },
    inferredItem: {
      reference: 'AUR-15601',
      title: 'Show the gate agent the queue length per group in real time',
      team: 'Airport Platform',
      closedAt: '2026-02-23',
      effort: 5,
      score: 0.79,
      commitDetail: 'AUR-15601 appears in 3 of 15 commits in R26.02-18.',
    },
    claimedSharePercent: 0.5,
    claimStatement:
      'Ground Operations claims half, noting that the share kept rising in April and May after load factor had stabilised.',
    claimedAt: '2026-05-11T08:30:00.000Z',
    corroborated: true,
    corroborationNote:
      'Airport Operations accepted the claim on 18 May on the strength of the post-stabilisation months.',
    verdictState: 'Consistent',
    verdictHeadline: 'Consistent, confounder material',
    verdictStatement:
      'The share boarding inside four minutes rose from 63% to 84% and kept rising after load factor stabilised, which is the team’s best argument. A 1.8pp load-factor fall across the same window is material and has not been normalised out, so the release is consistent with the movement rather than measured against it.',
    verdictConfidence: 'Medium',
  }),
];

const CHAINS_CARE: OutcomeTraceChain[] = [
  deliveredChain({
    key: 'care',
    outcome: OUT_CARE,
    productRef: CARE,
    releaseCode: 'R26.01-21',
    releaseName: 'Give the agent the whole trip on one screen',
    plannedDate: '2026-01-08',
    actualDate: '2026-01-14',
    workItemCount: 26,
    unlinkedWorkItemCount: 21,
    declaredBy: 'Chantelle Duval (Head of Customer Care)',
    declaredAt: '2026-01-15T12:00:00.000Z',
    registryObservedAt: '2026-01-06T09:45:00.000Z',
    evidenceDetail:
      'Product Registry field `targetOutcome` = OUT-CARE-01, recorded with a threshold of "74% within two quarters".',
    releaseCaveat:
      'Disruption volume was lower than FY25, and disrupted passengers are the multi-contact cases that hold this rate down.',
    declaredItem: {
      reference: 'AUR-15388',
      title: 'Assemble booking, baggage, loyalty and prior contacts into one agent view',
      team: 'Customer Care Platform',
      closedAt: '2026-01-07',
      effort: 34,
    },
    inferredItem: {
      reference: 'AUR-15421',
      title: 'Let the agent rebook and refund without leaving the contact',
      team: 'Customer Care Platform',
      closedAt: '2026-01-12',
      effort: 21,
      score: 0.92,
      commitDetail: 'AUR-15421 appears in 11 of 26 commits in R26.01-21.',
    },
    claimedSharePercent: 0.65,
    claimStatement:
      'Customer Care claims 65%, on the grounds that the rate crossed the pre-registered 74% threshold and did so on non-disruption contacts as well as disrupted ones.',
    claimedAt: '2026-05-04T11:00:00.000Z',
    corroborated: true,
    corroborationNote:
      'The Value Steward accepted the claim on 14 May, having checked the rate on non-disruption contacts separately.',
    verdictState: 'Measured',
    verdictHeadline: 'Measured movement, strong link',
    verdictStatement:
      'Declared in advance against a stated threshold, and the threshold was crossed. The lower disruption volume is a real confounder, but the rate rose on non-disruption contacts too, which is where the confounder has no purchase.',
    verdictConfidence: 'High',
  }),
];

const CHAINS_SMS: OutcomeTraceChain[] = [
  deliveredChain({
    key: 'sms',
    outcome: OUT_SMS,
    productRef: SMS,
    releaseCode: 'R25.11-09',
    releaseName: 'Route findings to an accountable owner on creation',
    plannedDate: '2025-11-13',
    actualDate: '2025-11-19',
    workItemCount: 12,
    unlinkedWorkItemCount: 9,
    declaredBy: 'Capt. Rhys Morgan (Director Safety)',
    declaredAt: '2025-11-20T08:00:00.000Z',
    registryObservedAt: '2025-11-11T14:30:00.000Z',
    evidenceDetail: 'Product Registry field `targetOutcome` = OUT-SMS-01, set before the release.',
    releaseCaveat:
      'The Safety Review Board also moved from monthly to fortnightly in January, which shortens closure time by itself.',
    declaredItem: {
      reference: 'AUR-15201',
      title: 'Assign every Level 1 finding an accountable owner when it is raised',
      team: 'Safety Systems',
      closedAt: '2025-11-12',
      effort: 8,
    },
    inferredItem: {
      reference: 'AUR-15233',
      title: 'Escalate a finding to the fleet chief at day 21, not day 45',
      team: 'Safety Systems',
      closedAt: '2025-11-17',
      effort: 5,
      score: 0.86,
      commitDetail: 'AUR-15233 appears in 4 of 12 commits in R25.11-09.',
    },
    claimedSharePercent: 0.5,
    claimStatement:
      'Safety claims half, splitting the improvement evenly with the move to a fortnightly review board.',
    claimedAt: '2026-04-30T16:00:00.000Z',
    corroborated: true,
    corroborationNote:
      'The Safety Review Board accepted the even split on 7 May as a reasonable division it could not measure more precisely.',
    verdictState: 'Consistent',
    verdictHeadline: 'Consistent, two changes together',
    verdictStatement:
      'Median closure fell from 46 to 33 days. Two deliberate changes overlap — owner-on-creation in November and a fortnightly board from January — and the even split between them is an agreed judgement rather than a measurement.',
    verdictConfidence: 'Medium',
  }),
];

const CHAINS_NET: OutcomeTraceChain[] = [
  deliveredChain({
    key: 'net',
    outcome: OUT_NET,
    productRef: NET,
    releaseCode: 'R26.02-07',
    releaseName: 'Protect the connecting bank at the four bases',
    plannedDate: '2026-02-11',
    actualDate: '2026-02-16',
    workItemCount: 22,
    unlinkedWorkItemCount: 18,
    declaredBy: 'Priya Raman (VP Network Planning)',
    declaredAt: '2026-02-17T09:30:00.000Z',
    registryObservedAt: '2026-02-09T10:00:00.000Z',
    evidenceDetail: 'Product Registry field `targetOutcome` = OUT-NET-01, set before the release.',
    releaseCaveat:
      'The FY25 Q4 baseline contains the worst weather month in five years, which flatters any comparison drawn against it.',
    declaredItem: {
      reference: 'AUR-15533',
      title: 'Hold spare airframe and crew against the two densest connecting banks',
      team: 'Network Systems',
      closedAt: '2026-02-10',
      effort: 21,
    },
    inferredItem: {
      reference: 'AUR-15566',
      title: 'Cancel the thinnest leg first when a bank cannot be flown whole',
      team: 'Network Systems',
      closedAt: '2026-02-14',
      effort: 8,
      score: 0.81,
      commitDetail: 'AUR-15566 referenced in the R26.02-07 manifest and in 5 commits.',
    },
    claimedSharePercent: 0.35,
    claimStatement:
      'Network Planning claims about a third, and says explicitly that a mild spring does more of the work than the standby policy.',
    claimedAt: '2026-05-13T10:45:00.000Z',
    corroborated: false,
    corroborationNote:
      'Flight Operations has asked to see the figure against a weather-normalised baseline before agreeing. Outstanding.',
    verdictState: 'Consistent',
    verdictHeadline: 'Consistent, baseline flatters',
    verdictStatement:
      'Completion factor rose from 98.46% to 99.08%, close to the 99.3% commitment. The baseline quarter contains an exceptional weather month, so part of the rise is the comparison rather than the operation, and Network Planning has said so rather than rebasing quietly.',
    verdictConfidence: 'Medium',
  }),
];

const CHAINS_SUS: OutcomeTraceChain[] = [
  deliveredChain({
    key: 'sus',
    outcome: OUT_SUS,
    productRef: SUS,
    releaseCode: 'R26.03-15',
    releaseName: 'Measured uplift replaces booked fuel in the emissions ledger',
    plannedDate: '2026-03-18',
    actualDate: '2026-03-26',
    workItemCount: 19,
    unlinkedWorkItemCount: 15,
    declaredBy: 'Dr Sanne Broekhuis (Head of Flight Efficiency)',
    declaredAt: '2026-03-27T13:20:00.000Z',
    registryObservedAt: '2026-03-16T08:15:00.000Z',
    evidenceDetail: 'Product Registry field `targetOutcome` = OUT-SUS-01, set before the release.',
    releaseCaveat:
      'The outcome commits to constant network and constant fleet mix. Both moved inside the window and this series is not yet normalised for either.',
    declaredItem: {
      reference: 'AUR-15655',
      title: 'Take uplift from the fuelling record rather than the flight plan',
      team: 'Flight Efficiency',
      closedAt: '2026-03-17',
      effort: 13,
    },
    inferredItem: {
      reference: 'AUR-15690',
      title: 'Attribute CO2 to the sector rather than to the rotation',
      team: 'Flight Efficiency',
      closedAt: '2026-03-24',
      effort: 8,
      score: 0.84,
      commitDetail: 'AUR-15690 appears in 6 of 19 commits in R26.03-15.',
    },
    claimedSharePercent: 0.2,
    claimStatement:
      'Flight Efficiency claims only a fifth, and is explicit that this release improved the measurement rather than the emissions.',
    claimedAt: '2026-05-15T09:10:00.000Z',
    corroborated: true,
    corroborationNote:
      'The Value Steward accepted the 20% and recorded the distinction: a better ledger is not a lower carbon figure, and the series should not be read as if it were.',
    verdictState: 'ClaimOnly',
    verdictHeadline: 'Claim only, measurement changed',
    verdictStatement:
      'CO2 per RPK fell across the window, but this release changed how the number is produced — measured uplift rather than booked fuel — and the outcome’s constant-network, constant-fleet condition was not held. What can be said is that the ledger is now closer to the truth, not that less carbon was burned because of it.',
    verdictConfidence: 'Low',
  }),
];

const CHAINS_BY_OUTCOME: Record<string, OutcomeTraceChain[]> = {
  [OUT_CDR.id]: CHAINS_CDR,
  [OUT_BAGJ.id]: CHAINS_BAGJ,
  [OUT_BOOK.id]: CHAINS_BOOK,
  [OUT_FUEL.id]: CHAINS_FUEL,
  [OUT_DSCM.id]: CHAINS_DSCM,
  [OUT_LOYL.id]: [],
  [OUT_TURN.id]: [],
  [OUT_MRO.id]: CHAINS_MRO,
  [OUT_FPD.id]: CHAINS_FPD,
  [OUT_PRM.id]: CHAINS_PRM,
  [OUT_CCC.id]: CHAINS_CCC,
  [OUT_ANC.id]: CHAINS_ANC,
  [OUT_CKI.id]: CHAINS_CKI,
  [OUT_CARE.id]: CHAINS_CARE,
  [OUT_SMS.id]: CHAINS_SMS,
  [OUT_NET.id]: CHAINS_NET,
  [OUT_SUS.id]: CHAINS_SUS,
};

const MOVEMENT_BY_OUTCOME: Record<string, OutcomeMovement> = {
  [OUT_CDR.id]: MOVEMENT_CDR,
  [OUT_BAGJ.id]: MOVEMENT_BAGJ,
  [OUT_BOOK.id]: MOVEMENT_BOOK,
  [OUT_FUEL.id]: MOVEMENT_FUEL,
  [OUT_DSCM.id]: MOVEMENT_DSCM,
  [OUT_LOYL.id]: {
    metric: metric('MET-231', 'Member share of wallet', 0.41, 'percent', {
      desiredDirection: 'Up',
      comparisonValue: 0.39,
      comparisonLabel: 'FY25 full year',
      confidence: 'Medium',
      qualityScore: 68,
    }),
    baselineLabel: 'FY25 full year',
    baselineValue: 0.39,
    baselineUnavailableReason: null,
    currentValue: 0.41,
    deltaAbsolute: 0.02,
    deltaPercent: 0.0513,
    direction: 'Improved',
    target: 0.45,
    targetUnavailableReason: null,
    measurable: true,
    unmeasurableReason: null,
    definitionVersionAtBaseline: 'v4.0',
    definitionVersionNow: 'v4.0',
    definitionChangedAt: null,
    definitionChangeNote: null,
    series: series(
      [
        ['2025-09', 0.39],
        ['2025-10', 0.39],
        ['2025-11', 0.4],
        ['2025-12', 0.4],
        ['2026-01', 0.4],
        ['2026-02', 0.41],
        ['2026-03', 0.41],
        ['2026-04', 0.41],
        ['2026-05', 0.41],
      ],
      'v4.0',
    ),
    confounders: [
      {
        label: 'A competitor left three of our four base markets',
        detail:
          'Members had fewer alternatives from January. Commercial regards this as the dominant driver.',
        occurredAt: '2026-01-05',
      },
    ],
  },
  [OUT_TURN.id]: {
    metric: metric('MET-236', 'On-time departure (D0)', 0.79, 'percent', {
      desiredDirection: 'Up',
      comparisonValue: 0.81,
      comparisonLabel: 'FY25 Q4 average',
      confidence: 'High',
      qualityScore: 86,
    }),
    baselineLabel: 'FY25 Q4 average (Oct–Dec 2025)',
    baselineValue: 0.81,
    baselineUnavailableReason: null,
    currentValue: 0.79,
    deltaAbsolute: -0.02,
    deltaPercent: -0.0247,
    direction: 'Worsened',
    target: 0.84,
    targetUnavailableReason: null,
    measurable: true,
    unmeasurableReason: null,
    definitionVersionAtBaseline: 'v4.0',
    definitionVersionNow: 'v4.0',
    definitionChangedAt: null,
    definitionChangeNote: null,
    series: series(
      [
        ['2025-09', 0.82],
        ['2025-10', 0.81],
        ['2025-11', 0.8],
        ['2025-12', 0.82],
        ['2026-01', 0.8],
        ['2026-02', 0.79],
        ['2026-03', 0.78],
        ['2026-04', 0.79],
        ['2026-05', 0.79],
      ],
      'v4.0',
    ),
    confounders: [],
  },
  [OUT_MRO.id]: MOVEMENT_MRO,
  [OUT_FPD.id]: MOVEMENT_FPD,
  [OUT_PRM.id]: MOVEMENT_PRM,
  [OUT_CCC.id]: MOVEMENT_CCC,
  [OUT_ANC.id]: MOVEMENT_ANC,
  [OUT_CKI.id]: MOVEMENT_CKI,
  [OUT_CARE.id]: MOVEMENT_CARE,
  [OUT_SMS.id]: MOVEMENT_SMS,
  [OUT_NET.id]: MOVEMENT_NET,
  [OUT_SUS.id]: MOVEMENT_SUS,
};

// ── The populations that are the point ───────────────────────────────────────

const SHIPPED_WORK_ITEMS = 1284;

function share(count: number): number {
  return Number((count / SHIPPED_WORK_ITEMS).toFixed(4));
}

const UNLINKED_WORK: UnlinkedWorkGroup[] = [
  {
    reason: 'Shipped in a release whose notes name no outcome',
    count: 268,
    sharePercent: share(268),
    examples: [
      workItem(
        'AUR-15119',
        'Move the seat map to the new rendering pipeline',
        'Enhancement',
        'Shopping & Offer',
        '2026-03-12',
        13,
      ),
      workItem(
        'AUR-15364',
        'Support three-letter city codes in the disruption search box',
        'Enhancement',
        'Customer Care Platform',
        '2026-04-01',
        3,
      ),
      workItem(
        'AUR-14688',
        'Bulk crew-swap screen for the OCC',
        'Feature',
        'Crew Platform',
        '2026-02-05',
        21,
      ),
    ],
    remedy:
      'The release note template has an optional `targetOutcome` field. Making it required at release cut would move most of this group into declared links within one quarter — and would expose which releases genuinely have no outcome, which is also worth knowing.',
    expected: false,
  },
  {
    reason: 'Regulatory or mandated work, which serves no product outcome by design',
    count: 141,
    sharePercent: share(141),
    examples: [
      workItem(
        'AUR-14520',
        'EU261 compensation letter wording — 2026 revision',
        'Compliance',
        'Customer Care Platform',
        '2026-01-30',
        8,
      ),
      workItem(
        'AUR-15078',
        'EASA duty-limit rule change effective April 2026',
        'Compliance',
        'Crew Platform',
        '2026-03-09',
        21,
      ),
    ],
    remedy:
      'Nothing to fix. This work is counted so that the unlinked total is not read as 617 items of waste — it is 348 items of waste and 269 items that were never supposed to have an outcome.',
    expected: true,
  },
  {
    reason: 'Keep-the-lights-on: patching, dependency upgrades, capacity work',
    count: 128,
    sharePercent: share(128),
    examples: [
      workItem(
        'AUR-15402',
        'Upgrade the booking cluster to PostgreSQL 17',
        'TechnicalDebt',
        'Data Platform',
        '2026-04-14',
        13,
      ),
      workItem(
        'AUR-14812',
        'Rotate the payment gateway certificates',
        'Operational',
        'Payments Platform',
        '2026-02-10',
        null,
        'Payments Platform records operational items without an estimate. Unknown effort, not zero.',
      ),
    ],
    remedy:
      'Nothing to fix at the item level. If this share grows past a quarter of throughput it is an argument about capacity, not about traceability.',
    expected: true,
  },
  {
    reason: 'The release named an outcome, but the field held its default value',
    count: 80,
    sharePercent: share(80),
    examples: [
      workItem(
        'AUR-15511',
        'Retry logic for the loyalty accrual queue',
        'TechnicalDebt',
        'Loyalty Engineering',
        '2026-04-20',
        5,
      ),
      workItem(
        'AUR-15597',
        'Cargo booking: accept dangerous-goods declarations as PDF',
        'Feature',
        'Cargo Systems',
        '2026-04-27',
        8,
      ),
    ],
    remedy:
      'The registry pre-fills `targetOutcome` with the product’s first outcome. The inference rule refuses to trust an unedited default, so these are reported as unlinked rather than counted as declared. Removing the pre-fill is a one-line change in the Product Registry.',
    expected: false,
  },
];

const OUTCOMES_WITHOUT_DELIVERY: OutcomeWithoutDelivery[] = [
  {
    outcome: OUT_LOYL,
    reason:
      'No release in FY26 H1 names OUT-LOYL-01, and no work item carries it. Skyward Loyalty shipped eleven releases in the period; all eleven left the outcome field at its default.',
    lastLinkedDeliveryAt: '2025-08-19',
    movementNote:
      'Member share of wallet rose from 39% to 41% anyway. Commercial attributes this to a competitor withdrawing from three base markets in January. Nothing Aurora built is known to have contributed.',
  },
  {
    outcome: OUT_TURN,
    reason:
      'Turn Management has shipped no release since 14 November 2025. The team was moved to the Airport Resource Management replacement in December.',
    lastLinkedDeliveryAt: '2025-11-14',
    movementNote:
      'On-time departure fell from 81% to 79% over the same period. An outcome with a named owner, a governed metric, a committed target of 84%, and nobody working on it.',
  },
];

const COVERAGE: DeliveryCoverage = {
  periodLabel: PERIOD_LABEL,
  shippedWorkItemCount: SHIPPED_WORK_ITEMS,
  shippedReleaseCount: 96,
  outcomeCount: 38,
  // The ten outcomes added in round 6 bring one declared and one
  // reference-inferred work item each, so twenty items move out of NotLinked
  // and ten into each of those two buckets. The four still sum to
  // SHIPPED_WORK_ITEMS, which is the only property of this list that matters:
  // the screen prints "accounted for exactly once" underneath it.
  linkTypeCounts: [
    { type: 'DeclaredByTeam', count: 224 },
    { type: 'InferredFromReference', count: 341 },
    { type: 'InferredFromTiming', count: 122 },
    { type: 'NotLinked', count: 597 },
  ],
};

/**
 * The four figures the screen leads with.
 *
 * The unlinked count is first and is not phrased as a coverage percentage. "48%
 * of delivery is traceable" is the same fact stated so that it sounds like
 * progress; "617 shipped items reach no outcome" is the fact stated so that
 * somebody does something about it.
 */
const KPIS: MetricValue[] = [
  // 597, not 617: it must equal the NotLinked bucket in COVERAGE, or the
  // figure the page leads with disagrees with the reconciliation it prints
  // three panels below.
  metric('MET-941', 'Shipped work reaching no outcome', 597, 'count', {
    desiredDirection: 'Down',
    comparisonValue: 664,
    comparisonLabel: 'FY25 H2',
    confidence: 'High',
    qualityScore: 88,
  }),
  metric('MET-942', 'Links resting on timing alone', 122, 'count', {
    desiredDirection: 'Down',
    comparisonValue: 96,
    comparisonLabel: 'FY25 H2',
    confidence: 'High',
    qualityScore: 88,
  }),
  // 16 of the 17 traceable outcomes carry a measurable movement; Baggage
  // Journey is the one refusal and still is.
  metric('MET-943', 'Outcomes with a measured movement', 16, 'count', {
    desiredDirection: 'Up',
    comparisonValue: 5,
    comparisonLabel: 'FY25 H2',
    confidence: 'Medium',
    qualityScore: 71,
  }),
  metric('MET-944', 'Outcomes with no delivery behind them', 11, 'count', {
    desiredDirection: 'Down',
    comparisonValue: 9,
    comparisonLabel: 'FY25 H2',
    confidence: 'High',
    qualityScore: 82,
  }),
];

// ── Repository ───────────────────────────────────────────────────────────────

export const DEFAULT_OUTCOME_ID = OUT_CDR.id;

/** The picker's options. Exported so the page can render it before data lands. */
export function listTraceableOutcomes(): ProductOutcomeRef[] {
  return OUTCOMES;
}

export interface OutcomesRepository {
  getOutcomeTraceability(outcomeId?: string): Promise<QueryResult<OutcomeTraceabilityView>>;
}

export function createOutcomesRepository(): OutcomesRepository {
  return {
    async getOutcomeTraceability(outcomeId?: string) {
      const selected = OUTCOMES.find((entry) => entry.id === outcomeId) ?? OUT_CDR;
      const movement = MOVEMENT_BY_OUTCOME[selected.id];
      const chains = CHAINS_BY_OUTCOME[selected.id] ?? [];

      // Defensive rather than decorative: the record is keyed by generated id
      // and a typo in a seed string would otherwise surface as a blank movement
      // panel rather than as an error.
      if (!movement) {
        throw new Error(
          `No movement fixture for outcome ${selected.code}. Every outcome in the picker must carry one, including the ones with no delivery.`,
        );
      }

      const data: OutcomeTraceabilityView = {
        periodLabel: PERIOD_LABEL,
        kpis: KPIS,
        outcomes: OUTCOMES,
        selectedOutcomeId: selected.id,
        movement,
        chains,
        coverage: COVERAGE,
        unlinkedWork: UNLINKED_WORK,
        outcomesWithoutDelivery: OUTCOMES_WITHOUT_DELIVERY,
      };

      return {
        data,
        meta: outcomesMeta({
          correlationId: `outcomes-trace-${selected.code.toLowerCase()}`,
          sources: [
            { name: 'Work Tracker', status: 'Available', recordCount: 1284 },
            { name: 'Product Registry', status: 'Available', recordCount: 38 },
            // Partial, and the screen shows why: three of five months of MET-221
            // never arrived, which is the whole of the Fuel Optimisation story.
            { name: 'Aurora Data Hub', status: 'Partial', recordCount: 31 },
          ],
          qualityScore: movement.measurable ? 74 : 46,
          openIssueCount: movement.measurable ? 3 : 5,
          confidence: movement.measurable ? 'Medium' : 'Low',
        }),
      };
    },
  };
}

export type { ClaimedContribution, OutcomeTraceChain, ProductOutcomeRef };
